import {Card, Button, Avatar, Box, Typography, CardContent, CardActions, CardMedia, ListItem, ListItemAvatar,
	ListItemText, alpha} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import React, {ReactElement, useEffect, useState} from "react";
import { Cursor } from "../../fstn/cursor";
import { Network } from "../../fstn/network";
import { DynamicCss } from "../../utility/dynamicCss";
import { State } from "../../fstn/state";
import Header from "./Header";
import Footer from "./Footer";
import { selectFocus } from "../../redux/selectors/editor";
import { connect } from "react-redux";
import { ICharacterData, INoteData, IParagraphData, IScriptElement, parseScript, ScriptElementData}
	from "../script/parseScript";
import { Phone, TouchApp, Videocam } from "@mui/icons-material";
import { updateFocus } from "../../redux/slices/editor";
import {selectProjects} from "../../redux/selectors/firebase";
import {Projects} from "../../redux/types/firebase";

interface IProps
{
	focus: string
	cursor: Cursor
	network: Network
	project: string
	projects: Projects
	updateFocus: (selected: string) => void
}

const useScriptStyles = makeStyles(theme => ({
	actions: {
		display: "flex",
		flexDirection: "column"
	},
	avatar: {
		height: theme.spacing(8),
		width: theme.spacing(8),
		backgroundColor: theme.palette.interTitle.dark,
		color: theme.palette.interTitle.contrastText,
	},
	button: {
		color: "inherit",
	},
	buttons: {
		alignItems: "center",
		display: "flex",
		flexDirection: "column",
	},
	card: {
		margin: theme.spacing(2),
		display: "flex",
		flexDirection: "column",
		flexGrow: 1
	},
	content: {
		display: "flex",
		flexDirection: "column",
		flexGrow: 1,
		marginRight: 2,
		overflowY: "auto",
		'&::-webkit-scrollbar': {
			width: '0.4em'
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: alpha(theme.palette.interTitle.main,0.2),
			borderRadius: '0.4em'
		}
	},
	reverseIcon: {
		height: "2em",
		width: "2em",
		borderRadius: theme.spacing(4),
		padding: theme.spacing(1),
		color: theme.palette.interTitle.contrastText,
		backgroundColor: theme.palette.interTitle.main
	},
	character: {
		marginLeft: theme.spacing(2),
		fontSize: 'x-large',
		textAlign: "left"
	},
	characterLine: {
		textAlign: "left",
		textShadow: "inherit",
		fontSize: "large",
		marginLeft: theme.spacing(2)
	},
	cut: {
		fontWeight: "bold",
		textTransform: "uppercase",
	},
	gesture: {
	},
	heading: {
		fontWeight: "bold",
		textTransform: "uppercase",
	},
	line: {
		textAlign: "center",
		textShadow: "inherit",
		fontVariant: "small-caps",
		fontSize: "large",
		marginLeft: theme.spacing(2)
	},
	note: {
		marginTop: 12,
		marginBottom: 12,
		textAlign: "center"
	},
	title: {
		fontSize: "x-large",
		marginBottom: 12,
		textAlign: "center",
	},
	listItem: {
		paddingTop: 0,
	},
	screen: {
		position: 'absolute',
		display: 'flex',
		flexDirection: "column",
		top: 0,
		bottom: 0,
		width: "100%",
	},
	still: {
		height: "100%"
	},
}));

function Intertitle({ cursor, network, project, projects, updateFocus }: IProps)
{
	const classes = useScriptStyles();
	const projectDetails = projects[project];
	const state = network.states.get(cursor.state) as State;
	const [, setState] = useState(cursor.state);
	const scriptElements = parseScript(state.script);
	const trigger = (triggerName: string) =>
	{
		return () =>
		{
			cursor.trigger(triggerName)
			updateFocus(cursor.state);
			setState(cursor.state);
		}
	}

	const renderers: { [type: string]: (element: IScriptElement, key: string, index: number) => ReactElement; } = {
		character: (element: IScriptElement, key: string, index: number) =>
		{
			const data = element.data as ICharacterData;
			const image = `${data.character.toLowerCase()}.png`;
			const icons: { [key: string]: ReactElement } = {
				phone: <Phone />,
				voicemail: <Phone />
			};
			const icon = icons[data.character.toLowerCase()];

			return (
				<ListItem key={`${key}_${index}`} className={classes.listItem}>
					<ListItemAvatar>
						{icon ?
							<Avatar className={classes.reverseIcon}>{icon}</Avatar> :
							<Avatar
								className={classes.avatar}
								src={`${projectDetails.imageSource}/intertitle/characters/${image}`} />
						}
					</ListItemAvatar>
					<ListItemText>
						<Typography className={classes.character} variant={"body1"} component="span">{data.character}</Typography>
						{data.parenthetical &&
							<Typography className={classes.character} variant={"body2"} component="span">{`(${data.parenthetical})`}</Typography>
						}

						<Typography key={index} variant={"body2"} className={classes.characterLine}>{(
							data.lines.reduce((previous, current) =>
								(previous += " " + current)) || '\u00A0') as string
						}</Typography>

					</ListItemText>
				</ListItem>
			);
		},
		cut: (element: IScriptElement, key: string, index: number) =>
		{
			return (
				<ListItem key={`${key}_${index}`} className={classes.listItem}>
					<ListItemAvatar>
						<Avatar className={classes.reverseIcon}><Videocam /></Avatar>
					</ListItemAvatar>
					<ListItemText>
						<Typography className={classes.character} variant={"body1"}>CUT TO</Typography>

						<Typography key={index} variant={"body2"} className={classes.characterLine}>{(element.data || '\u00A0') as string}</Typography>

					</ListItemText>
				</ListItem>);
		},
		gesture: (element: IScriptElement, key: string, index: number) =>
		{
			const gestures = element.data as ScriptElementData[];

			return (
				<Box key={`${key}_${index}`} className={classes.buttons}>
					{gestures.map((gesture, index) => (
						<Button className={classes.button} key={index} onClick={trigger(gesture.text)}>
							<TouchApp className="buttonIcon" />{gesture.text}
						</Button>)
					)}
				</Box>
			);
		},
		line: (element: IScriptElement, key: string, index: number) =>
		{
			return (
				<Typography key={`${key}_${index}`} variant="body1" className={classes.line}>
					{(element.data || '\u00A0') as string}
				</Typography>
			);
		},
		paragraph: (element: IScriptElement, key: string, index: number) =>
		{
			const data = element.data as IParagraphData;

			return (
				<Box key={`${key}_${index}`}>
					{data.lines.map((line, index) =>
					{
						return <Typography key={`${key}_${index}`} variant="body1" className={classes.line}>
							{(line || '\u00A0') as string}
						</Typography>
					})}
				</Box>
			);
		},
		title: (element: IScriptElement, key: string, index: number) =>
		{
			return (
				<Typography key={`${key}_${index}`} variant="body2" className={[classes.title, "title"].join(" ")}>
					{(element.data || '\u00A0') as string}
				</Typography>
			);
		},
		note: (element: IScriptElement, key: string, index: number) =>
		{
			const data = element.data as INoteData;

			return (
				<Box className={classes.note} key={`${key}_${index}`}>
					<Typography className={classes.heading} component="span">{data.note} - </Typography>
					<Typography className={classes.line} component="span">{data.line}</Typography>
				</Box>
			);
		},
		conditional: (element: IScriptElement, key: string, index: number) =>
		{
			const options = element.data as ScriptElementData[];

			return (
				<Box key={`${key}_${index}`} className={classes.buttons}>
					{options.filter(option => {
						try {
							return cursor.testExpression(option.condition);
						} catch (error: any) {
							console.error(error.message);
							return false;
						}
					}).map((option, index) => (
							<Button className={classes.button} key={index} onClick={trigger(option.text)}>
								{option.text}
							</Button>)
						)}
				</Box>
			);
		},
		option: (element: IScriptElement, key: string, index: number) =>
		{
			const options = element.data as ScriptElementData[];

			return (
				<Box key={`${key}_${index}`} className={classes.buttons}>
					{options.map((option, index) => (
						<Button className={classes.button} key={index} onClick={trigger(option.text)}>
							{option.text}
						</Button>)
					)}
				</Box>
			);
		},
		still: (element: IScriptElement, key: string, index: number) =>
		{
			const data = element.data as ScriptElementData;

			return (
				<CardMedia key={`${key}_${index}`} className={classes.still}
						   image={`${projectDetails.imageSource}/stills/${data.text}`} />
			);
		}
	};

	useEffect(() =>
	{
		projectDetails.styleSource && DynamicCss.load(projectDetails.styleSource, "intertitle", true);
	}, [projectDetails.styleSource]);

	useEffect(()=>{
		if(cursor.paused){
			cursor.play();
		}
	}, [cursor])

	const createElement = (line: IScriptElement, index: number) =>
	{
		if (renderers[line.type])
		{
			return renderers[line.type](line, cursor.state, index);
		}
		return null;
	};

	const contentTypes = scriptElements.reduce((types, current) =>
	{
		if (current.type === "gesture" || current.type === "option" || current.type === 'conditional')
		{
			types.interaction.push(current);
		}
		else
		{
			types.content.push(current);
		}
		return types;
	}, { interaction: [], content: [] } as { interaction: IScriptElement[], content: IScriptElement[] })

	state.actions.forEach(action => {
		if (action.action === "AddTouchGesture") {
			if (! contentTypes.interaction.find(interaction => interaction.type === "gesture" && interaction.data)) {
				contentTypes.interaction.push({
					type: "gesture",
					data:[{
						text:action.parameters.trigger.value(cursor)
					}],
					mappedRange:[]
				});
			}
		}
	});
	return (
		<Box className={classes.screen}>
			<Card className={[classes.card, "card"].join(" ")}>
				<Header imageSource={projectDetails.imageSource} />
				<CardContent className={`${classes.content} content`}>
					{contentTypes.content.map(createElement)}
				</CardContent>
				<CardActions className={classes.actions}>
					{contentTypes.interaction.length === 0 && cursor.triggers.length === 1 &&
						<Button className={classes.button} onClick={trigger(cursor.triggers[0])}>
							{cursor.triggers[0]}
						</Button>}
					{contentTypes.interaction.map(createElement)}
				</CardActions>
				<Footer imageSource={projectDetails.imageSource} />
			</Card>
		</Box>
	);
}

const mapStateToProps = (store: never) => ({
	focus: selectFocus(store),
	projects: selectProjects(store),
});

export default connect(mapStateToProps, {
	updateFocus,
})(Intertitle);